import { useField } from 'formik';
import { FocusEventHandler, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useToastStore from '@/store/toast';

const useFormField = <T>(fieldName: string, fallbackValue: T) => {
  const { t } = useTranslation();
  const showToast = useToastStore((s) => s.showToast);

  const [
    { value: formikValue, onBlur },
    { touched, error: formikError },
    { setValue: formikSetValue, setTouched },
  ] = useField<T>(fieldName);

  const value = useMemo(() => {
    return formikValue;
  }, [formikValue]);

  const error = useMemo(() => {
    return touched && formikError ? t(`errors:form.${formikError}`) : undefined;
  }, [formikError, t, touched]);

  const setValue = useCallback(
    async (value: T) => {
      await formikSetValue(value);
    },
    [formikSetValue],
  );

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    if (!touched) {
      setTouched(true).catch(() => {
        showToast('Ein Fehler ist aufgetreten', 'error');
      });
    }
    onBlur(e);
  };

  return {
    value: value ?? fallbackValue,
    error,
    onBlur: handleBlur,
    touched,
    setValue,
  };
};

export default useFormField;
